<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import {
  ActionSheet,
  ActionSheetAction,
  showConfirmDialog,
  showNotify,
  Tab,
  Tabs,
} from "vant";
import { computed, onActivated, onBeforeMount, ref, watch } from "vue";
import SocialListItem from "@/components/mobile/SocialListItem.vue";
import AdmireListItem from "@/components/mobile/AdmireListItem.vue";
import { useFollow } from "@/stores/follow";
import { useAdmire } from "@/stores/admire";
import { dateDifference } from "@/modules/common";
import { onCurrentMateChange } from "@/stores/internal-subscribe";
import { useMate } from "@/stores/mate";
import { useRoute, useRouter } from "vue-router";
import { startChatSession } from "@/components/mobile/chat/chatUtils";
import { storeToRefs } from "pinia";
import i18n from "@/locales";

const hashList = ["#following", "#follower"];

interface AdmireActionSheet extends ActionSheetAction {
  toCancelId?: number;
}

const actions: AdmireActionSheet[] = [
  {
    name: i18n.global.t("Social.actions.cancelGuarding"),
    color: "#f56c6c",
    callback: () => {
      showNotify({
        type: "success",
        message: i18n.global.t("Notifications.guardingCanceled"),
      });
    },
  },
];
// 状态
const router = useRouter();
const route = useRoute();
const mateStore = useMate();
const followStore = useFollow();
const { followingMap } = storeToRefs(followStore);
const admireStore = useAdmire();
const currentTab = ref(0);
const isLoading = ref(false);
const showActionSheet = ref(false);

// Method
const prepareData = async () => {
  isLoading.value = true;
  try {
    // if (currentTab.value === 0) {
    //   await admireStore.getAdmiringList();
    // } else if (currentTab.value === 1) {
    //   await admireStore.getAdmiringList();
    //   await admireStore.getAdmirerList();
    // }
    if (currentTab.value === 0) {
      await followStore.getFollowingList();
    } else if (currentTab.value === 1) {
      await followStore.getFollowingList();
      await followStore.getFollowerList();
    }
  } finally {
    isLoading.value = false;
  }
};
// Lifecycle
watch(currentTab, () => {
  prepareData();
});
onCurrentMateChange(async () => {
  followStore.disableCache(true);
  admireStore.disableCache(true);
  await prepareData();
});
onBeforeMount(() => {
  const tabIdxFromHash = hashList.indexOf(route.hash);
  currentTab.value = tabIdxFromHash === -1 ? 0 : tabIdxFromHash;
  prepareData();
});
onActivated(() => {
  prepareData();
});
// EventHandler
const onFollowClick = async (
  isFollowing: boolean,
  mateId: number,
  mateName: string
): Promise<number | null> => {
  if (isFollowing) {
    await showConfirmDialog({
      title: i18n.global.t("ConfirmModal.unfollowTitle"),
      message: i18n.global.t("ConfirmModal.sureUnfollowText", {
        name: `@${mateName}`,
      }),
      confirmButtonText: i18n.global.t("Buttons.confirm"),
      cancelButtonText: i18n.global.t("Buttons.cancel"),
    });
    await followStore.unfollow(followingMap.value.get(mateId)!);
    followStore.following.list = followStore.following.list.filter(
      (item) => item.mateId !== mateId
    );
    return null;
  } else {
    const record = await followStore.follow(mateId);
    followStore.following.list.unshift(record);
    return record.id!;
  }
};
const onChatClick = async ({ id, uuid }: Mate) => {
  await startChatSession(id!, uuid);
};
const onTabChang = (newTabIdx: number) => {
  router.replace({ hash: hashList[newTabIdx] });
};
const onArrowClick = (id: number) => {
  actions[0].toCancelId = id;
  showActionSheet.value = true;
};
const onActionSheetSelect = async (item: AdmireActionSheet) => {
  try {
    item.loading = true;
    await admireStore.cancelAdmire(item.toCancelId!);
    item.toCancelId = undefined;
  } finally {
    item.loading = false;
  }
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr]">
    <threads-nav :show-center="false" show-back />
    <div class="w-full overflow-hidden">
      <tabs
        v-model:active="currentTab"
        class="h-full grid grid-rows-[auto_1fr]"
        swipeable
        @change="onTabChang"
      >
        <tab
          v-if="false"
          :title="$t('Social.admiring')"
          class="overflow-y-auto"
        >
          <div>
            <div
              v-if="admireStore.admiring.list.length > 0"
              class="flex flex-col w-full"
            >
              <admire-list-item
                v-for="item in admireStore.admiring.list"
                :id="item.id!"
                :key="item.id"
                v-nsfw="item.mate.isNsfw"
                :avatar-url="item.mate.mediaUrl"
                :duration="
                  dateDifference(new Date(item.dtCreate!), new Date()).days
                "
                :enable-commit="!mateStore.isMyMate(item.mate.uuid)"
                :gender="item.mate.gender"
                :is-admiring="true"
                :mate-id="item.mateId"
                :name="item.mate.name"
                :rating="item.hp"
                :type="item.mate.type"
                :uuid="item.mate.uuid"
                class="relative"
                @chat-click="onChatClick(item.mate)"
                @commit-click="
                  $router.push({
                    name: 'threadCommit',
                    params: { uuid: item.mate.uuid },
                  })
                "
                @arrow-click="onArrowClick"
              />
            </div>
            <div
              v-else-if="isLoading"
              class="h-[200px] flex items-center justify-center"
            >
              <span class="loading"></span>
            </div>
            <div
              v-else
              class="flex flex-col items-center justify-center h-[200px] w-full"
            >
              <div class="flex flex-col items-center justify-center">
                <div class="text-2xl font-bold">
                  {{ $t("Social.admiringTitle") }}
                </div>
                <div class="text-neutral-400">
                  {{ $t("Social.admiringDesc") }}
                </div>
              </div>
            </div>
          </div>
        </tab>
        <tab v-if="false" :title="$t('Social.admirer')" class="overflow-y-auto">
          <div>
            <div
              v-if="admireStore.admirer.list.length > 0"
              class="flex flex-col w-full"
            >
              <admire-list-item
                v-for="item in admireStore.admirer.list"
                :id="item.id!"
                :key="item.id"
                v-nsfw="item.fan.isNsfw"
                :avatar-url="item.fan.mediaUrl"
                :duration="
                  dateDifference(new Date(item.dtCreate!), new Date()).days
                "
                :enable-commit="false"
                :enableArrowBtn="true"
                :gender="item.fan.gender"
                :is-admiring="false"
                :is-admiring-card="false"
                :mate-id="item.fanId"
                :name="item.fan.name"
                :rating="item.hp"
                :type="item.fan.type"
                :uuid="item.fan.uuid"
                class="relative"
                @chat-click="onChatClick(item.fan)"
                @arrow-click="onArrowClick"
                @commit-click="
                  $router.push({
                    name: 'threadCommit',
                    params: { uuid: item.fan.uuid },
                  })
                "
              />
            </div>
            <div
              v-else-if="isLoading"
              class="h-[200px] flex items-center justify-center"
            >
              <span class="loading"></span>
            </div>
            <div
              v-else
              class="flex flex-col items-center justify-center h-[200px] w-full"
            >
              <div class="flex flex-col items-center justify-center">
                <div class="text-2xl font-bold">
                  {{ $t("Social.admirerTitle") }}
                </div>
                <div class="text-neutral-400">
                  {{ $t("Social.admirerDesc") }}
                </div>
              </div>
            </div>
          </div>
        </tab>
        <tab :title="$t('Social.following')" class="overflow-y-auto">
          <div>
            <div
              v-if="followStore.following.list.length > 0"
              class="flex flex-col w-full"
            >
              <social-list-item
                v-for="item in followStore.following.list"
                :id="item.id!"
                :key="item.id"
                v-nsfw="item.mate.isNsfw"
                :avatar-url="item.mate.mediaUrl"
                :content="item.mate.bio"
                :follow-click="
                  () =>
                    onFollowClick(
                      followingMap.has(item.mateId),
                      item.mateId,
                      item.mate.name
                    )
                "
                :gender="0"
                :is-following="followingMap.has(item.mateId)"
                :mate-id="item.mateId"
                :name="item.mate.name"
                :type="item.mate.type"
                class="relative"
                @click="
                  $router.push({
                    name: 'mateHome',
                    params: { uuid: item.mate.uuid },
                  })
                "
              />
            </div>
            <div
              v-else-if="isLoading"
              class="h-[200px] flex items-center justify-center"
            >
              <span class="loading"></span>
            </div>
            <div
              v-else
              class="flex flex-col items-center justify-center h-[200px] w-full"
            >
              <div class="flex flex-col items-center justify-center">
                <div class="text-2xl font-bold">
                  {{ $t("Social.followingTitle") }}
                </div>
                <div class="text-neutral-400">
                  {{ $t("Social.followingDesc") }}
                </div>
              </div>
            </div>
          </div>
        </tab>
        <tab :title="$t('Social.followers')" class="overflow-y-auto">
          <div>
            <div
              v-if="followStore.follower.list.length > 0"
              class="flex flex-col w-full"
            >
              <social-list-item
                v-for="item in followStore.follower.list"
                :id="item.id!"
                :key="item.id"
                v-nsfw="item.fan.isNsfw"
                :avatar-url="item.fan.mediaUrl"
                :content="item.fan.bio"
                :follow-click="
                  () =>
                    onFollowClick(
                      followingMap.has(item.fanId),
                      item.fanId,
                      item.fan.name
                    )
                "
                :gender="item.fan.gender"
                :is-following="followingMap.has(item.fanId)"
                :mate-id="item.fanId"
                :name="item.fan.name"
                :type="item.fan.type"
                class="relative"
                @click="
                  $router.push({
                    name: 'mateHome',
                    params: { uuid: item.fan.uuid },
                  })
                "
              />
            </div>
            <div
              v-else-if="isLoading"
              class="h-[200px] flex items-center justify-center"
            >
              <span class="loading"></span>
            </div>
            <div
              v-else
              class="flex flex-col items-center justify-center h-[200px]"
            >
              <div class="flex flex-col items-center justify-center">
                <div class="text-2xl font-bold">
                  {{ $t("Social.followersTitle") }}
                </div>
                <div class="text-neutral-400">
                  {{ $t("Social.followersDesc") }}
                </div>
              </div>
            </div>
          </div>
        </tab>
      </tabs>
    </div>
    <action-sheet
      v-model:show="showActionSheet"
      :actions="actions"
      :cancel-text="$t('Buttons.cancel')"
      close-on-click-action
      @cancel="showActionSheet = false"
      @select="onActionSheetSelect"
    />
  </div>
</template>
